import type { FmProductChromeDescriptor } from './FmProductPage.types';

type UnknownRecord = Record<string, unknown>;

function fail(path: string, message: string): never {
  throw new Error(`FmProductPage: ${path} ${message}`);
}

function record(value: unknown, path: string): UnknownRecord {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    fail(path, 'must be an object');
  }
  return value as UnknownRecord;
}

function exact(value: UnknownRecord, allowed: readonly string[], path: string): void {
  const unexpected = Object.keys(value).filter((key) => !allowed.includes(key));
  if (unexpected.length > 0) fail(path, `contains unknown key "${unexpected[0]}"`);
}

function text(value: unknown, path: string): string {
  if (typeof value !== 'string' || value.trim() === '') fail(path, 'must be a non-empty string');
  return value;
}

function callback(value: unknown, path: string): void {
  if (typeof value !== 'function') fail(path, 'must be a callback');
}

function registerId(value: unknown, path: string, ids: Set<string>): string {
  const id = text(value, path);
  if (ids.has(id)) fail(path, `duplicates id "${id}"`);
  ids.add(id);
  return id;
}

function entries(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value) || value.length === 0) fail(path, 'must contain at least 1 item');
  return value;
}

function validateNavigation(
  value: unknown,
  path: string,
  ids: Set<string>,
  chapterIds: ReadonlySet<string>,
): void {
  entries(value, path).forEach((entry, index) => {
    const itemPath = `${path}[${index}]`;
    const item = record(entry, itemPath);
    exact(item, ['id', 'label', 'chapterId'], itemPath);
    registerId(item.id, `${itemPath}.id`, ids);
    text(item.label, `${itemPath}.label`);
    const chapterId = text(item.chapterId, `${itemPath}.chapterId`);
    if (!chapterIds.has(chapterId)) fail(`${itemPath}.chapterId`, 'must reference a product chapter');
  });
}

function validateLanguage(value: unknown, path: string): void {
  const language = record(value, path);
  exact(language, ['value', 'ariaLabel', 'options', 'onChange'], path);
  const selected = text(language.value, `${path}.value`);
  text(language.ariaLabel, `${path}.ariaLabel`);
  callback(language.onChange, `${path}.onChange`);
  const optionValues = new Set<string>();
  entries(language.options, `${path}.options`).forEach((entry, index) => {
    const itemPath = `${path}.options[${index}]`;
    const option = record(entry, itemPath);
    exact(option, ['value', 'label'], itemPath);
    const optionValue = text(option.value, `${itemPath}.value`);
    if (optionValues.has(optionValue)) fail(`${itemPath}.value`, 'must be unique');
    optionValues.add(optionValue);
    text(option.label, `${itemPath}.label`);
  });
  if (!optionValues.has(selected)) fail(`${path}.value`, 'must reference a language option');
}

function validateIdentity(value: unknown, path: string): void {
  const identity = record(value, path);
  const status = text(identity.status, `${path}.status`);
  if (status === 'signed-out') {
    exact(identity, ['status', 'loginLabel', 'registerLabel', 'onLogin', 'onRegister'], path);
    text(identity.loginLabel, `${path}.loginLabel`);
    text(identity.registerLabel, `${path}.registerLabel`);
    callback(identity.onLogin, `${path}.onLogin`);
    callback(identity.onRegister, `${path}.onRegister`);
    return;
  }
  if (status !== 'signed-in') fail(`${path}.status`, 'must be signed-out or signed-in');
  exact(identity, ['status', 'accountLabel', 'dashboardLabel', 'logoutLabel', 'onDashboard', 'onLogout'], path);
  ['accountLabel', 'dashboardLabel', 'logoutLabel'].forEach((key) => text(identity[key], `${path}.${key}`));
  callback(identity.onDashboard, `${path}.onDashboard`);
  callback(identity.onLogout, `${path}.onLogout`);
}

function validateAuthDialog(value: unknown, path: string): void {
  const dialog = record(value, path);
  exact(dialog, ['open', 'mode', 'title', 'context', 'closeLabel', 'onClose', 'onAuthenticated'], path);
  if (typeof dialog.open !== 'boolean') fail(`${path}.open`, 'must be a boolean');
  if (!['login', 'register'].includes(String(dialog.mode))) fail(`${path}.mode`, 'must be login or register');
  ['title', 'context', 'closeLabel'].forEach((key) => {
    if (dialog[key] !== undefined) text(dialog[key], `${path}.${key}`);
  });
  callback(dialog.onClose, `${path}.onClose`);
  callback(dialog.onAuthenticated, `${path}.onAuthenticated`);
}

export function validateFmProductChrome(
  value: unknown,
  path: string,
  ids: Set<string>,
  chapterIds: ReadonlySet<string>,
): asserts value is FmProductChromeDescriptor {
  const chrome = record(value, path);
  exact(chrome, [
    'brandLabel', 'onHome', 'navigationAriaLabel', 'menuAriaLabel',
    'openMenuLabel', 'closeMenuLabel', 'navigation', 'language', 'identity', 'authDialog',
  ], path);
  ['brandLabel', 'navigationAriaLabel', 'menuAriaLabel', 'openMenuLabel', 'closeMenuLabel']
    .forEach((key) => text(chrome[key], `${path}.${key}`));
  callback(chrome.onHome, `${path}.onHome`);
  validateNavigation(chrome.navigation, `${path}.navigation`, ids, chapterIds);
  validateLanguage(chrome.language, `${path}.language`);
  validateIdentity(chrome.identity, `${path}.identity`);
  validateAuthDialog(chrome.authDialog, `${path}.authDialog`);
}
